import { FormattedMessage, useIntl } from "react-intl";
import {
  BRAND_COMMUNICATION_TONES,
  BRAND_CULTURAL_REFERENCE_LEVELS,
  BRAND_EMOJI_USAGES,
  BRAND_SENTENCE_COMPLEXITIES,
  BRAND_SOCIAL_INTENSITY_LEVELS,
  BRAND_VOCABULARY_STYLES,
  BRAND_VOICE_ATTRIBUTES,
} from "@runcheetah/shared/brand-profile";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@runcheetah/ui";

import type {
  AudienceSegment,
  BrandVoice,
  EditableBrandFields,
  VoiceFieldKey,
} from "./confirm-review-data";
import { buildAudienceFields, CONTENT_DNA_FIELDS, VOICE_FIELDS } from "./confirm-review-data";
import { ConfirmReviewCard } from "./confirm-review-card";
import { EditableTagCollection } from "./editable-tag-collection";

function AudienceSegments({
  items,
  onSave,
}: {
  items: AudienceSegment[];
  onSave: (fields: EditableBrandFields) => Promise<void> | void;
}) {
  const intl = useIntl();

  return (
    <EditableTagCollection
      items={items.map((item) => item.title).filter(Boolean)}
      onSave={(next) => onSave(buildAudienceFields(next.map((title) => ({ title }))))}
      emptyLabel={intl.formatMessage({
        id: "brandOnboarding.confirm.noAudienceSegments",
        defaultMessage: "No audience segments surfaced yet.",
      })}
      addLabel={intl.formatMessage({
        id: "brandOnboarding.confirm.addAudience",
        defaultMessage: "Add audience",
      })}
      testIdPrefix="audience-chip"
    />
  );
}

const formatOptionLabel = (option: string): string => option.replaceAll("-", " ");

function VoiceSection({
  voice,
  onSave,
}: {
  voice: BrandVoice;
  onSave: (fields: EditableBrandFields) => Promise<void> | void;
}) {
  const intl = useIntl();
  const fields: Array<{
    key: VoiceFieldKey;
    label: string;
    description: string;
    options: readonly string[];
  }> = [
    {
      key: "tone",
      label: intl.formatMessage({ id: "brandVoice.tone.label", defaultMessage: "Tone" }),
      description: intl.formatMessage({
        id: "brandVoice.tone.description",
        defaultMessage: "Overall emotional temperature of the messaging.",
      }),
      options: BRAND_COMMUNICATION_TONES,
    },
    {
      key: "voice",
      label: intl.formatMessage({ id: "brandVoice.voice.label", defaultMessage: "Voice" }),
      description: intl.formatMessage({
        id: "brandVoice.voice.description",
        defaultMessage: "The personality trait that should come through consistently.",
      }),
      options: BRAND_VOICE_ATTRIBUTES,
    },
    {
      key: "vocabularyStyle",
      label: intl.formatMessage({
        id: "brandVoice.vocabulary.label",
        defaultMessage: "Vocabulary",
      }),
      description: intl.formatMessage({
        id: "brandVoice.vocabulary.description",
        defaultMessage: "How plainspoken, expert, or elevated the wording should feel.",
      }),
      options: BRAND_VOCABULARY_STYLES,
    },
    {
      key: "sentenceComplexity",
      label: intl.formatMessage({
        id: "brandVoice.sentenceComplexity.label",
        defaultMessage: "Sentence Complexity",
      }),
      description: intl.formatMessage({
        id: "brandVoice.sentenceComplexity.description",
        defaultMessage: "Whether the writing should feel fast and direct or more layered.",
      }),
      options: BRAND_SENTENCE_COMPLEXITIES,
    },
    {
      key: "contentDnaHumorLevel",
      label: intl.formatMessage({ id: "brandVoice.humor.label", defaultMessage: "Humor" }),
      description: intl.formatMessage({
        id: "brandVoice.contentDna.humorLevelDescription",
        defaultMessage: "How much social content should lean into humor.",
      }),
      options: BRAND_SOCIAL_INTENSITY_LEVELS,
    },
    {
      key: "useOfEmoji",
      label: intl.formatMessage({
        id: "brandVoice.emojiUsage.label",
        defaultMessage: "Emoji Usage",
      }),
      description: intl.formatMessage({
        id: "brandVoice.emojiUsage.description",
        defaultMessage: "Whether emojis are part of the brand voice or should be rare.",
      }),
      options: BRAND_EMOJI_USAGES,
    },
    {
      key: "culturalReferences",
      label: intl.formatMessage({
        id: "brandVoice.culturalReferences.label",
        defaultMessage: "Cultural References",
      }),
      description: intl.formatMessage({
        id: "brandVoice.culturalReferences.description",
        defaultMessage: "How current, niche, or culture-aware the brand should sound.",
      }),
      options: BRAND_CULTURAL_REFERENCE_LEVELS,
    },
  ];

  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      {fields.map((field) => (
        <div
          key={field.key}
          className="space-y-2 rounded-2xl border border-border/70 bg-background/70 p-3 shadow-sm"
        >
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-foreground">{field.label}</p>
            <p className="line-clamp-2 text-xs leading-5 text-muted-foreground">
              {field.description}
            </p>
          </div>
          <Select
            value={voice[field.key] ?? ""}
            onValueChange={(value) => onSave({ [VOICE_FIELDS[field.key]]: value })}
          >
            <SelectTrigger className="h-9 w-full rounded-xl bg-card">
              <SelectValue
                placeholder={intl.formatMessage({
                  id: "brandVoice.selectPlaceholder",
                  defaultMessage: "Select...",
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((option) => (
                <SelectItem key={option} value={option}>
                  <span className="capitalize">{formatOptionLabel(option)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

export function AudienceVoiceAnglesStep({
  audienceSegments,
  voice,
  contentAngles,
  onSave,
}: {
  audienceSegments: AudienceSegment[];
  voice: BrandVoice;
  contentAngles: string[];
  onSave: (fields: EditableBrandFields) => Promise<void> | void;
}) {
  const intl = useIntl();

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-3">
      <ConfirmReviewCard
        label={
          <FormattedMessage
            id="brandOnboarding.confirm.audience"
            defaultMessage="Audience segments"
          />
        }
      >
        <AudienceSegments items={audienceSegments} onSave={onSave} />
      </ConfirmReviewCard>

      <ConfirmReviewCard
        label={
          <FormattedMessage id="brandOnboarding.confirm.voice" defaultMessage="Writing style" />
        }
      >
        <VoiceSection voice={voice} onSave={onSave} />
      </ConfirmReviewCard>

      <ConfirmReviewCard
        label={
          <FormattedMessage
            id="brandOnboarding.confirm.contentAngles"
            defaultMessage="Content angles"
          />
        }
      >
        <EditableTagCollection
          items={contentAngles}
          onSave={(next) => onSave({ [CONTENT_DNA_FIELDS.contentAngles]: next })}
          emptyLabel={intl.formatMessage({
            id: "brandOnboarding.confirm.noContentAngles",
            defaultMessage: "No content angles surfaced yet.",
          })}
          addLabel={intl.formatMessage({
            id: "brandOnboarding.confirm.addAngle",
            defaultMessage: "Add angle",
          })}
          testIdPrefix="angle-chip"
        />
      </ConfirmReviewCard>
    </div>
  );
}
